import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  ArrowLeft,
  PencilIcon,
  MoreVertical,
  Search,
  ChevronDown,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import vendor_profile_icon from "../../../assets/profile_icon.svg";
import VendorPurchase from "./VendorPurchase";
import AdvancePayment from "./AdvancePayment";
import { BASE_URL } from "utils/constant";
import PurchaseOrder from "../Procurement/PurchaseOrder";
import { date } from "yup";
import { set } from "date-fns";
import UpdateVendorModal from "./VendorUpdate";
import { useAlert } from "components/UI_components/Alert";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

const VendorDetails = ({ vendor, onClose, onVendorDeleted }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("Recently Added");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [purchases, setPurchases] = useState([]);
  const [activeForm, setActiveForm] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [totalPayment, setTotalPayment] = useState(0);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [advancePayment, setAdvancePayment] = useState(
    vendor?.advance_payment || 0,
  );
  const [vendor_name, setVendorName] = useState(vendor?.name || "");
  const [vendor_contact, setVendorContact] = useState(vendor?.contact_no || "");
  const [vendor_address, setVendorAddress] = useState(vendor?.address || "");
  const [vendor_type, setVendorType] = useState(vendor?.vendor_type || "");
  const [pendingPayment, setPendingPayment] = useState(0);
  const { addAlert } = useAlert();
  const [isDeletingVendor, setIsDeletingVendor] = useState(false);
  const [openAdvanceMenu, setOpenAdvanceMenu] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleAdvanceMenuClickOutside = (event) => {
      const target = event.target;
      if (
        !(
          target &&
          typeof target.closest === "function" &&
          target.closest(".advance-menu-wrapper")
        )
      ) {
        setOpenAdvanceMenu(null);
      }
    };

    document.addEventListener("mousedown", handleAdvanceMenuClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleAdvanceMenuClickOutside);
    };
  }, []);

  const getVendor = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/get-a-vendor/${vendor.id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const data = await response.json();
      if (data.status) {
        setAdvancePayment(data.data.advance_payment || 0);
        setVendorName(data.data.name || "");
        setVendorContact(data.data.contact_no || "");
        setVendorAddress(data.data.address || "");
        setVendorType(data.data.vendor_type || "");
      } else {
        console.error("Error fetching vendor details:", data.error);
      }
    } catch (error) {
      console.error("Error fetching vendor details:", error);
    }
  };

  useEffect(() => {
    getVendor();
  }, [vendor]);

  const fetchPurchases = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const organization = JSON.parse(localStorage.getItem("organization"));
      const response = await fetch(
        `${BASE_URL}/inventory/list-vendor-purchases/${vendor.id}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const data = await response.json();
      if (data.status) {
        let payment = 0;
        let pending = 0;
        const formattedPurchases = data.data.map((purchase) => {
          let badge = { label: purchase?.payment_status || "Pending Approval" };
          if (purchase.type === "Advance Payment") {
            badge = { label: "Advance" };
            return {
              code: organization?.organization_name + `_Advpay_` + purchase.id,
              site: "Organisation Purchase",
              createdOn: purchase.created_on || purchase.payment_date || null,
              badge: badge,
              details: {
                advanceAmount: `₹ ${purchase?.amount?.toLocaleString()}`,
                paidBy: purchase.created_by || "N/A",
                date: new Date(purchase.payment_date).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  },
                ),
                paymentMethod:
                  purchase.payment_method || purchase.payment_mode || "N/A",
                receipt: purchase.receipt || "",
              },
            };
          }
          const paidAmount = parseFloat(purchase.paid_amount || 0);
          const totalAmount = parseFloat(purchase.total_price || 0);
          payment += paidAmount;
          pending += totalAmount - paidAmount;
          return {
            po: purchase,
            code: purchase.po_number,
            rfq_number: purchase.rfq_number || "",
            site: purchase?.site_name || "Organisation Purchase",
            createdOn: purchase.created_on || purchase.order_date || null,
            badge: badge,
            details: {
              paidBy: purchase.paid_by_name || "N/A",
              date: new Date(purchase.order_date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }),
              materials:
                purchase.lines[0]?.material_name +
                (purchase.lines.length > 1
                  ? ` + ${purchase.lines.length - 1}`
                  : ""),
              total: `₹ ${totalAmount?.toLocaleString()}`,
              paid: `₹ ${paidAmount?.toLocaleString()}`,
              due: `₹ ${(totalAmount - paidAmount)?.toLocaleString()}`,
            },
          };
        });
        setTotalPayment(payment);
        setPendingPayment(pending);
        console.log(formattedPurchases);
        setPurchases(formattedPurchases);
      } else {
        console.error("Error fetching purchases:", data.error);
      }
    } catch (error) {
      console.error("Error fetching purchases:", error);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, [vendor]);

  // Opens the modal
  const handleDeleteVendor = () => {
    setShowDeleteModal(true);
  };

  // Called when user confirms inside the modal
  const handleConfirmDelete = async () => {
    if (!vendor?.id) return;
    try {
      setIsDeletingVendor(true);
      const accessToken = localStorage.getItem("access");
      const response = await fetch(
        `${BASE_URL}/inventory/delete-vendor/${vendor.id}/`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      let data = null;
      try {
        data = await response.json();
      } catch { }

      if (!response.ok || data?.status === false) {
        throw new Error(
          data?.message || data?.error || "Failed to delete vendor",
        );
      }

      addAlert("Vendor deleted successfully", "success");
      setShowDeleteModal(false);
      if (typeof onVendorDeleted === "function") onVendorDeleted();
      onClose();
    } catch (error) {
      setDeleteError(error.message || "Error deleting vendor");
      addAlert(error.message || "Error deleting vendor", "error");
    } finally {
      setIsDeletingVendor(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleSortChange = (option) => {
    setSortOption(option);
    setDropdownOpen(false);
  };

  const getBadgeStyle = (badge) => {
    switch (badge) {
      case "Full Due":
        return "bg-red-100 text-red-600";
      case "Full Paid":
        return "bg-green-100 text-green-600";
      case "Part Payment":
        return "bg-green-100 text-green-600";
      case "Partial Paid":
        return "bg-yellow-100 text-yellow-600";
      case "Advance":
        return "bg-purple-100 text-purple-600";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Advance receipt PDF – structured similar to Purchase Order receipt
  const stylesPDF = StyleSheet.create({
    page: {
      padding: 30,
      backgroundColor: "#FFFFFF",
    },
    header: {
      marginBottom: 20,
      borderBottomWidth: 1,
      borderBottomColor: "#CCCCCC",
      paddingBottom: 10,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    headerLeft: {
      flexDirection: "column",
    },
    title: {
      fontSize: 20,
      fontWeight: "bold",
      color: "#46237A",
    },
    subtitle: {
      fontSize: 12,
      color: "#666666",
      marginTop: 4,
    },
    section: {
      marginTop: 16,
      marginBottom: 4,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: "bold",
      marginBottom: 6,
      color: "#333333",
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 4,
      paddingVertical: 3,
      borderBottomWidth: 1,
      borderBottomColor: "#EEEEEE",
    },
    label: {
      fontSize: 10,
      color: "#666666",
    },
    value: {
      fontSize: 10,
      textAlign: "right",
      color: "#232323",
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingVertical: 3,
    },
    summaryLabel: {
      fontSize: 11,
      color: "#333333",
    },
    summaryValue: {
      fontSize: 11,
      textAlign: "right",
      color: "#333333",
    },
    summaryLink: {
      fontSize: 11,
      textAlign: "right",
      color: "#2563EB",
      textDecoration: "underline",
    },
    totalRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: 8,
      paddingTop: 8,
      borderTopWidth: 1,
      borderTopColor: "#000000",
    },
    totalLabel: {
      fontSize: 12,
      fontWeight: "bold",
    },
    totalValue: {
      fontSize: 12,
      fontWeight: "bold",
      textAlign: "right",
    },
    footer: {
      marginTop: 24,
      borderTopWidth: 1,
      borderTopColor: "#CCCCCC",
      paddingTop: 10,
      fontSize: 8,
      color: "#999999",
      textAlign: "center",
    },
  });

  const AdvanceReceiptPDF = ({
    vendorName,
    code,
    date,
    paidBy,
    amount,
    paymentMethod,
    receiptUrl,
  }) => {
    const formattedAmount = Number(amount || 0);

    return (
      <Document>
        <Page size="A4" style={stylesPDF.page}>
          {/* Header */}
          <View style={stylesPDF.header}>
            <View style={stylesPDF.headerLeft}>
              <Text style={stylesPDF.title}>Advance Payment Receipt</Text>
              <Text style={stylesPDF.subtitle}>
                Reference No: {code || "N/A"}
              </Text>
              <Text style={stylesPDF.subtitle}>Date: {date || "N/A"}</Text>
            </View>
          </View>

          {/* Vendor Details */}
          <View style={stylesPDF.section}>
            <Text style={stylesPDF.sectionTitle}>Vendor Details</Text>
            <View style={stylesPDF.row}>
              <Text style={stylesPDF.label}>Vendor Name</Text>
              <Text style={stylesPDF.value}>{vendorName || "N/A"}</Text>
            </View>
          </View>

          {/* Payment Summary */}
          <View style={stylesPDF.section}>
            <Text style={stylesPDF.sectionTitle}>Payment Summary</Text>
            <View style={stylesPDF.summaryRow}>
              <Text style={stylesPDF.summaryLabel}>Advance Amount</Text>
              <Text style={stylesPDF.summaryValue}>
                Rs. {formattedAmount.toLocaleString("en-IN")}
              </Text>
            </View>
            <View style={stylesPDF.summaryRow}>
              <Text style={stylesPDF.summaryLabel}>Payment Method</Text>
              <Text style={stylesPDF.summaryValue}>
                {paymentMethod || "N/A"}
              </Text>
            </View>
            <View style={stylesPDF.totalRow}>
              <Text style={stylesPDF.totalLabel}>Grand Total</Text>
              <Text style={stylesPDF.totalValue}>
                Rs. {formattedAmount.toLocaleString("en-IN")}
              </Text>
            </View>
          </View>

          <View style={stylesPDF.footer}>
            <Text>
              This is a computer-generated receipt and does not require
              signature.
            </Text>
          </View>
        </Page>

        {/* Receipt Image Page */}
        {receiptUrl && (
          <Page size="A4" style={{ padding: 20 }}>
            <Text style={{ fontSize: 14, marginBottom: 10, color: "#333" }}>
              Receipt Image
            </Text>
            <Image
              src={receiptUrl}
              style={{
                width: "100%",
                height: "90%",
                objectFit: "contain",
              }}
            />
          </Page>
        )}
      </Document>
    );
  };

  const parseDate = (dateStr) => {
    const [day, monthStr, year] = dateStr.split(" ");
    const months = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };
    return new Date(year, months[monthStr], day);
  };

  const getBadgePriority = (label) => {
    switch (label) {
      case "Full Paid":
        return 1;
      case "Partial Paid":
        return 2;
      case "Part Payment":
        return 2;
      case "Full Due":
        return 3;
      case "Advance":
        return 4;
      default:
        return 5;
    }
  };

  const sortOptions = [
    "Recently Added",
    "Site Name (A-Z)",
    "Site Name (Z-A)",
    "Total Amount",
    "Total Paid",
    "Total Due",
    "Payment Status",
  ];

  const parseCurrency = (value) => {
    if (!value) return 0;
    return Number(value.replace(/[^0-9.-]+/g, ""));
  };

  const getTimeValue = (dateValue) => {
    const parsedDate = new Date(dateValue);
    return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
  };

  const filteredAndSortedPurchases = useMemo(() => {
    const filtered = purchases.filter((item) => {
      const searchTarget = `${item.code} ${item.site} ${item.details?.paidBy ?? ""
        } ${item.details?.materials ?? ""}`.toLowerCase();
      return searchTarget.includes(searchTerm);
    });

    return filtered.sort((a, b) => {
      switch (sortOption) {
        case "Recently Added":
          return getTimeValue(b.createdOn) - getTimeValue(a.createdOn);
        case "Site Name (A-Z)":
          return a.site?.localeCompare(b.site);
        case "Site Name (Z-A)":
          return b.site?.localeCompare(a.site);
        case "Payment Status":
          return (
            getBadgePriority(a.badge.label) - getBadgePriority(b.badge.label)
          );
        case "Total Amount":
          return (
            parseCurrency(b.details?.total) - parseCurrency(a.details?.total)
          );
        case "Total Paid":
          return (
            parseCurrency(b.details?.paid) - parseCurrency(a.details?.paid)
          );
        case "Total Due":
          return parseCurrency(b.details?.due) - parseCurrency(a.details?.due);

        default:
          return 0;
      }
    });
  }, [searchTerm, sortOption, purchases]);

  return (
    <>
      {activeForm === "purchase" && (
        <VendorPurchase
          onClose={() => {
            setActiveForm(null);
            fetchPurchases();
            getVendor();
          }}
          vendor={vendor}
        />
      )}
      {activeForm === "advancePayment" && (
        <AdvancePayment
          vendor={vendor}
          onClose={() => {
            setActiveForm(null);
            fetchPurchases();
            getVendor();
          }}
        />
      )}

      {selectedPurchase && (
        <PurchaseOrder
          po={selectedPurchase.po}
          poCode={selectedPurchase.code}
          rfqId={selectedPurchase.rfq_number}
          vendor={vendor?.name || "N/A"}
          onClose={() => {
            setSelectedPurchase(null);
            fetchPurchases();
            getVendor();
          }}
        />
      )}
      {showUpdateModal && (
        <UpdateVendorModal
          vendor={{
            id: vendor.id,
            name: vendor_name,
            contact_no: vendor_contact,
            address: vendor_address,
            vendor_type: vendor_type,
          }}
          onClose={() => {
            setShowUpdateModal(false);
            fetchPurchases();
            getVendor();
          }}
          onUpdate={() => {
            fetchPurchases();
            getVendor();
          }}
        />
      )}

      {!activeForm && (
        <div className="w-full px-4 text-black mt-2 flex flex-col">
          <div className="shrink-0">
            <div className="border h-[230px] bg-white border-gray-200 rounded-[12px] p-4 mb-3">
              <div className="flex justify-between">
                <div className="flex gap-4">
                  <button
                    onClick={onClose}
                    className="text-[#232323] hover:text-black mt-1"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex gap-3">
                    <div className="w-10 h-10">
                      <img
                        src={vendor_profile_icon}
                        alt="Vendor Icon"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h1 className="text-[16px] text-[#232323]">
                          {vendor_name || "N/A"}
                        </h1>
                        <span className="text-xs px-2 py-0 bg-[#E3E3E3] text-[#737373] rounded-full font-medium border border-gray-300">
                          Vendor
                        </span>
                      </div>
                      <p className="text-[14px] text-gray-600">
                        {vendor_type || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-sm text-left mr-[300px]">
                    <p className="text-gray-600">
                      Address{" "}
                      <span className="ml-2"> : {vendor_address || "N/A"}</span>
                    </p>
                    <p className="text-gray-600">
                      Contact No :{" "}
                      <span className="text-[14px] text-[#232323]">
                        {vendor_contact || "N/A"}
                      </span>
                    </p>
                  </div>
                  <button
                    className="text-[#1C1B1F] hover:text-black disabled:opacity-50"
                    onClick={() => setShowUpdateModal(true)}
                    disabled={isDeletingVendor}
                  >
                    <PencilIcon className="w-5 h-5 mt-2" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800 mt-2 disabled:opacity-50"
                    onClick={handleDeleteVendor}
                    disabled={isDeletingVendor}
                    title="Delete vendor"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <hr className="my-3 border-gray-600" />

              <div className="ml-2 mt-3">
                <h2 className="text-[20px] text-[#232323] mb-3">
                  Payment Info
                </h2>

                <div className="flex gap-4 mb-2">
                  {[
                    {
                      label: "Total Payment",
                      value: `₹ ${totalPayment.toLocaleString() || 0}`,
                    },
                    {
                      label: "Advance Payment",
                      value: `₹ ${advancePayment.toLocaleString() || 0}`,
                    },
                    {
                      label: "Pending Payment",
                      value: `₹ ${pendingPayment.toLocaleString() || 0}`,
                    },
                    { label: "Refunded Payment", value: "₹ 0.00" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-white border border-gray-200 rounded-lg h-[65px] px-4 py-2 flex flex-col justify-center w-[170px]"
                    >
                      <p className="text-sm text-[#737373] mb-1">
                        {item.label}
                      </p>
                      <p className="text-[#232323] text-[16px] whitespace-nowrap">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mb-3">
              <p className="ml-2 text-sm text-gray-600">
                {purchases.length} total purchase
              </p>
              <div className="flex gap-3 items-center">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search in purchase"
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#3B0069]"
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
                <div className="relative w-[175px]" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="px-4 py-2 bg-[#ECE6F0] rounded-[6px] text-sm flex items-center justify-between text-[#3B0069] w-full"
                  >
                    {sortOption}
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute z-10 mt-1 bg-white border border-gray-200 rounded shadow w-full max-h-[175px] overflow-y-auto">
                      {sortOptions.map((option) => (
                        <div
                          key={option}
                          onClick={() => handleSortChange(option)}
                          className={`px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer ${option === sortOption
                            ? "bg-gray-100 font-medium"
                            : ""
                            }`}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setActiveForm("advancePayment")}
                  className="px-4 py-2 bg-white border border-[#3B0069] text-[#3B0069] rounded-lg text-sm flex items-center gap-2 hover:text-purple-700"
                >
                  <Plus size={16} />
                  Advance Payment
                </button>
                <button
                  onClick={() => setActiveForm("purchase")}
                  className="px-4 py-2 bg-[#3B0069] text-white rounded-lg text-sm flex items-center gap-2 hover:bg-purple-700"
                >
                  <Plus size={16} />
                  Purchase
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-y-auto overflow-x-hidden flex-grow space-y-[10px]">
            {filteredAndSortedPurchases.map((item, index) => {
              const label = item.badge.label;
              const isAdvance = label === "Advance";
              const isAdvanceMenuOpen = isAdvance && openAdvanceMenu === index;

              return (
                <div
                  key={index}
                  className="border border-gray-400 rounded-[12px] p-2 shadow-sm cursor-pointer bg-white"
                  onClick={() => {
                    if (!isAdvance) {
                      setSelectedPurchase(item);
                    }
                  }}
                >
                  <div className="flex items-start ml-4 gap-[50px]">
                    <div className="w-[200px] mr-[200px]">
                      <p className="text-[12px] text-[#737373]">
                        {isAdvance ? "Purchase Code" : "PO No."}
                      </p>
                      <p className="text-[#232323] text-[16px] whitespace-nowrap">
                        {item.code}
                      </p>
                    </div>

                    {isAdvance ? (
                      <>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">
                            Transaction Date
                          </p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.date}
                          </p>
                        </div>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">Paid By</p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.paidBy}
                          </p>
                        </div>
                        <div className="w-[200px] flex items-center gap-2 relative advance-menu-wrapper">
                          <span
                            className={`text-xs px-3 py-1 rounded-full font-medium ${getBadgeStyle(
                              label,
                            )}`}
                          >
                            {label}
                          </span>
                          <button
                            className="p-1 rounded-full hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenAdvanceMenu(
                                isAdvanceMenuOpen ? null : index,
                              );
                            }}
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                          {isAdvanceMenuOpen && (
                            <div className="absolute right-0 top-7 z-20 bg-white border border-gray-200 rounded shadow-md">
                              <PDFDownloadLink
                                document={
                                  <AdvanceReceiptPDF
                                    vendorName={vendor_name}
                                    code={item.code}
                                    date={item.details?.date}
                                    paidBy={item.details?.paidBy}
                                    amount={parseCurrency(
                                      item.details?.advanceAmount,
                                    )}
                                    paymentMethod={item.details?.paymentMethod}
                                    receiptUrl={item.details?.receipt}
                                  />
                                }
                                fileName={`Advance_Receipt_${item.code || "N-A"}.pdf`}
                                className="block whitespace-nowrap px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                              >
                                {({ loading }) =>
                                  loading ? "Generating..." : "Download Receipt"
                                }
                              </PDFDownloadLink>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">
                            Transaction Date
                          </p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.date}
                          </p>
                        </div>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">
                            Materials
                          </p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.materials}
                          </p>
                        </div>
                        <div className="w-[200px]">
                          <span
                            className={`text-xs px-3 py-1 rounded-full font-medium ${getBadgeStyle(
                              label,
                            )}`}
                          >
                            {label}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <hr className="my-2 border-gray-600" />

                  <div className="flex items-start ml-4 gap-[50px]">
                    <div className="w-[200px] mr-[200px]">
                      <span className="text-xs px-2 py-1 bg-[#ECE6F0] text-[#3B0069] rounded-full font-medium">
                        {item.site}
                      </span>
                    </div>

                    {isAdvance ? (
                      <>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">
                            Advance Amount
                          </p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.advanceAmount}
                          </p>
                        </div>
                        <div className="w-[200px]"></div>
                        <div className="w-[200px]"></div>
                      </>
                    ) : (
                      <>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">
                            Total Amount
                          </p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.total}
                          </p>
                        </div>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">
                            Total Paid
                          </p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.paid}
                          </p>
                        </div>
                        <div className="w-[200px]">
                          <p className="text-[12px] text-[#737373]">
                            Total Due
                          </p>
                          <p className="text-[#232323] text-[16px]">
                            {item.details.due}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <p className="text-base font-semibold text-gray-900">
                Delete Vendor
              </p>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteError("");
                }}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3 px-6 py-4 text-sm text-gray-700">
              <p>
                Are you sure you want to delete this vendor? This action cannot
                be undone.
              </p>
              {deleteError && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-100">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3 bg-gray-50">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteError("");
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={isDeletingVendor}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-70"
                disabled={isDeletingVendor}
              >
                {isDeletingVendor ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorDetails;
